import { LiaTransparentElement, installConfirmHandler } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { logout } from './shared/api/auth.js';
import { getUserName, isSignedIn, onSessionChange } from './shared/api/session.js';
import { ADMIN_PATH, LOGIN_PATH } from './shared/paths.js';
import { ROUTES, guard, owns } from './user/routes.js';
import { clearDeveloper, getDeveloper, onDeveloperChange } from './user/api/developerSession.js';
import { signOut } from './user/api/developer.js';
import { navigate, startRouter, subscribe } from './router.js';
import { ACCOUNTS_PATH, PROFILE_PATH, accountIcon, accountPath, isPersonal, sectionsOf, sectionOf } from './user/accounts.js';
import {
   addressedAccountId, clearAccounts, getAccounts, loadAccounts, onAccountsChange, rememberAccount
} from './user/state/context.js';

/**
 * `<user-root>` - the chrome around the customer application.
 *
 * <b>The application is about a person and their teams</b>, and the chrome says so: the sidebar is the
 * person's own page, the list of teams, and then every team they are part of with its pages underneath
 * - their personal one first, under its own name. The navbar holds the person and the sign-out and
 * nothing else. Which team a page is about is the address's business - see `user/state/context.js`.
 *
 * The administration shell's smaller sibling, and the differences are the ones that follow from what
 * this bundle is for. There is no time picker, because nothing here is about a window; there is no
 * search, because there is nothing to search; and the sign-in page is one of *this* application's
 * routes rather than the other's, because a visitor reaches this bundle first.
 *
 * It is a separate element rather than a parameterised one for the same reason the bundles are separate:
 * a shell that took "which application am I" as a flag would put both applications' imports into both
 * builds, which is precisely the thing being avoided.
 */
export class UserRoot extends LiaTransparentElement
{
   static properties = {
      match: { state: true },

      // Redrawn on a developer sign-in or sign-out too, so the chip, the menu and the impersonation
      // banner change with the credential rather than on the next navigation.
      developer: { state: true },

      // The accounts this person is part of, for the sidebar.
      accounts: { state: true }
   };

   constructor()
   {
      super();
      this.developer = getDeveloper();
      this.accounts = getAccounts();
   }

   connectedCallback()
   {
      super.connectedCallback();

      // Every irreversible action here - removing a member, withdrawing an invitation, revoking a token,
      // stepping down - confirms through a row action's `confirm`. Without this, the kit answers those
      // with the browser's own `window.confirm()`: a bare box with no verb on its button, and one that
      // blocks the page while it is open.
      this._unconfirm = installConfirmHandler();

      this._unsubscribe = subscribe(match =>
      {
         this.match = match;
         document.title = `${match.route.title} · covdbg`;
         void this.closeMobileSidebar();

         // The list is asked for on every page, so the sidebar shows what is true now - an invitation
         // accepted, a team handed over - rather than what was true when the tab was opened.
         if (this.developer)
         {
            void loadAccounts();
         }

         // A page about an account is the account the next visit opens on.
         if (sectionOf(match.path) && match.query.get('account'))
         {
            rememberAccount(match.query.get('account'));
         }
      });

      // A session that ends anywhere - an expired refresh token, a sign-out - sends the application to
      // the sign-in page from one place, rather than every caller having to notice.
      this._unwatch = onSessionChange(session =>
      {
         if (!session && !this.match?.route.anonymous && location.pathname !== LOGIN_PATH)
         {
            navigate(LOGIN_PATH, { replace: true });
         }
      });

      this._unwatchDeveloper = onDeveloperChange(session =>
      {
         this.developer = session;

         if (session)
         {
            void loadAccounts();
         }
         else
         {
            clearAccounts();
         }

         if (!session && !this.match?.route.anonymous && location.pathname !== LOGIN_PATH && !isSignedIn())
         {
            navigate(LOGIN_PATH, { replace: true });
         }
      });

      this._unaccounts = onAccountsChange(accounts => { this.accounts = accounts; });

      startRouter({ routes: ROUTES, guard, owns });
   }

   disconnectedCallback()
   {
      this._unconfirm?.();
      this._unsubscribe?.();
      this._unwatch?.();
      this._unwatchDeveloper?.();
      this._unaccounts?.();
      super.disconnectedCallback();
   }

   async closeMobileSidebar()
   {
      await this.querySelector('lia-app-shell')?.toggleSidebar(false);
   }

   /**
    * Sidebar entries and user-menu items raise the same cancelable `lia-navigate`.
    *
    * An absolute URL is left alone. Everything else goes to the router, which decides for itself whether
    * the path belongs to this bundle - `/admin` does not, and becomes a page load rather than a route
    * lookup that would land on the catch-all.
    */
   onNavigate(event)
   {
      const url = event.detail?.url ?? '';

      if (url === '' || /^[a-z]+:\/\//i.test(url))
      {
         return;
      }

      event.preventDefault();
      navigate(url);
   }

   /**
    * Signs out: the developer session is ended on the service first, so it does not linger on the
    * person's own sessions list until it expires, and then forgotten here whatever the service said.
    */
   async onLogout(event)
   {
      event.preventDefault();

      if (getDeveloper())
      {
         await signOut().catch(() => null);
      }

      clearDeveloper();
      clearAccounts();
      logout();
      navigate(LOGIN_PATH, { replace: true });
   }

   /**
    * Ends a borrowed view and goes back to where it was borrowed from.
    *
    * The operator's own session is untouched: impersonation is a second credential in this origin's
    * storage rather than a change of who they are, so dropping it leaves them signed in to the portal.
    */
   async leaveImpersonation()
   {
      // Ended on the service too: a borrowed session left to expire is half an hour in which the code
      // that was spent to get it is still worth something.
      await signOut().catch(() => null);
      clearDeveloper();
      location.assign(ADMIN_PATH);
   }

   /**
    * The sidebar: the person's own page, the list of teams, then every team with its pages underneath.
    *
    * The personal team comes first, under "Personal", with the three pages it has. Every other team is
    * a group under its own name: all of the sections for an owner, the overview alone for a member,
    * which is the one page the API answers to them. Members, invitations and owners sit side by side,
    * because the rail nests one level and no more. The page the address names is marked, and the rail
    * opens the group holding it by itself. A person owns at most ten teams, so the list is simply all of
    * them.
    */
   get navigation()
   {
      if (!this.developer)
      {
         return [];
      }

      const path = this.match?.path ?? '';
      const addressed = addressedAccountId();

      const page = (account, id, label) => ({
         id: `${account.id}:${id}`,
         label,
         url: accountPath(id, account.id),
         active: account.id === addressed && path === `/${id}`
      });

      const pages = account => sectionsOf(account).flatMap(entry => (entry.id === 'members'
         ? [
            page(account, 'members', 'Members'),
            page(account, 'members/invitations', 'Invitations'),
            page(account, 'members/owners', 'Owners')
         ]
         : [page(account, entry.id, entry.label)]));

      const personal = this.accounts.filter(entry => isPersonal(entry));
      const teams = this.accounts.filter(entry => !isPersonal(entry));

      return [
         { id: 'overview', label: 'Overview', icon: 'fa-solid fa-house', url: '/', active: path === '/' },
         { id: 'accounts', label: 'Teams', icon: 'fa-solid fa-people-group', url: ACCOUNTS_PATH, active: path === ACCOUNTS_PATH },
         ...personal.map(account => ({ id: account.id, label: 'Personal', icon: accountIcon(account), items: pages(account) })),
         ...teams.map(account => ({ id: account.id, label: account.name ?? account.id, icon: accountIcon(account), items: pages(account) }))
      ];
   }

   /** What the account chip opens: the person's own pages. */
   get userMenu()
   {
      // <b>It may not be empty</b>: `lia-navbar` renders the dropdown toggle for any signed-in user,
      // renders the menu only when there are entries, and initialises Bootstrap's `Dropdown` on the
      // toggle either way - so an empty menu is a control that throws when it is clicked.
      return this.developer
         ? [
            { id: 'profile', label: 'Your profile', icon: 'fa-solid fa-user', href: PROFILE_PATH },
            { id: 'sessions', label: 'Sessions', icon: 'fa-solid fa-plug', href: `${PROFILE_PATH}/sessions` },
            { id: 'decisions', label: 'Decisions', icon: 'fa-solid fa-list-check', href: `${PROFILE_PATH}/decisions` }
         ]
         : [{ id: 'home', label: 'Home', icon: 'fa-solid fa-house', href: '/' }];
   }

   render()
   {
      const match = this.match;

      if (!match)
      {
         return html``;
      }

      if (match.route.fullscreen)
      {
         return match.route.render(match);
      }

      return html`
         <lia-app-shell
            class="covdbg-customer"
            .showThemeToggle=${false}
            .searchEnabled=${false}
            brand-label="covdbg"
            brand-href="/"
            nav-label="Primary"
            content-class=""
            show-footer
            footer-text="covdbg - a Liasoft product. All rights reserved."
            .navItems=${this.navigation}
            .user=${{ loginname: this.developer?.email ?? getUserName() ?? 'account' }}
            .userMenu=${this.userMenu}
            @lia-navigate=${this.onNavigate}
            @lia-logout=${this.onLogout}
         >
            ${this.impersonationBanner}
            ${this.routed(match)}
         </lia-app-shell>`;
   }

   /**
    * The one thing a borrowed view must never let somebody forget.
    *
    * <b>Above the page rather than inside it</b>, on every route, and it names the operator - because
    * the failure this prevents is an operator answering a support question, walking away, and later
    * reading a screen they believe is their own. It is also the only control that ends the borrowing:
    * signing out would end the developer's session, which is not the operator's to end.
    */
   get impersonationBanner()
   {
      const borrowed = this.developer?.impersonatedBy;

      if (!borrowed)
      {
         return nothing;
      }

      return html`
         <lia-alert
            variant="warning"
            icon="fa-solid fa-user-secret"
            heading="You are viewing as somebody else"
            message=${`This is ${this.developer.email ?? 'a developer'}'s view of covdbg, borrowed by `
               + `${borrowed}. They can see this session on their own account page. It ends by itself `
               + 'shortly.'}
            .actions=${[{
               id: 'leave',
               label: 'Stop and return to the portal',
               variant: 'outline-secondary',
               size: 'sm'
            }]}
            @lia-action=${() => { void this.leaveImpersonation(); }}
         ></lia-alert>`;
   }

   /** The routed view, with a boundary around it. See the administration shell for why it is keyed. */
   routed(match)
   {
      return keyed(match.path, html`
         <lia-error-boundary
            global
            heading="This page could not be displayed"
            message="Something in this view failed to render. Your data is unaffected."
            show-details
            retry-label="Reload this page"
            .onError=${error => console.error('View render failed:', error)}
         >
            ${match.route.render(match)}
         </lia-error-boundary>`);
   }
}

customElements.define('user-root', UserRoot);
